import React, { useEffect, useState } from 'react';
import { Box, Center, Divider, HStack, Pressable, Skeleton, VStack } from 'native-base';
import { faBirthdayCake, faEnvelope, faHome, faPhone, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { Member, useAppSettings, useI18n, useSpringBigStore, useUpdateMember } from '@springbig/shared';
import { SpIcon } from '~/shared/Icon';
import { Caption, H1, H4 } from '~/shared/Typography';
import { useGeneralStyles, useProfileStyles, useReferStyles } from '~/shared/styles';
import { useAppNavigate } from '~/pages';
import Button from '~/shared/Button';
import Image from '~/shared/Image';
import dayjs from 'dayjs';
import Sheet from 'react-modal-sheet';
import Loading from '~/shared/Loading';
import QrCode from '~/shared/QrCode';

type ProfileHeaderProps = {
    member?: Member | null;
}

const ProfileHeader = ({ member }: ProfileHeaderProps) => {

    const { t } = useI18n();
    const { mutate: updateMember, isLoading: loadingUpload } = useUpdateMember();
    const { navigate, params } = useAppNavigate();
    const { profile } = useAppSettings();
    const {
        text,
        icon
    } = useProfileStyles();
    const gStyles = useGeneralStyles();
    const [profilePictureUri, setProfilePictureUri] = useState<string>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [profilePictureAdded, setProfilePictureAdded] = useState<boolean>(false);
    const [profilePictureRemoved, setProfilePictureRemoved] = useState<boolean>(false);
    const [undoType, setUndoType] = useState<string>();
    const { qr_code_color, qr_code_background } = useReferStyles();

    useEffect(() => {

        if (member && member.use_custom_profile_picture && member.profile_picture) {
            setProfilePictureUri(member.profile_picture);
        }
        else {
            setProfilePictureUri('');
        }
    }, [member]);

    useEffect(() => {

        if (profilePictureAdded) {
            setTimeout(() => {

                setProfilePictureAdded(false);
            }, 4000);
        }

        if (profilePictureRemoved) {
            setTimeout(() => {

                setProfilePictureRemoved(false);
            }, 4000);
        }
    }, [profilePictureAdded, profilePictureRemoved]);

    const fileToBase64 = (file) =>

        new Promise((resolve, reject) => {

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (e) => reject(e);
        });

    const onImageChange = async event => {

        if (event.target.files && event.target.files[0]) {
            const img = event.target.files[0];
            const base64 = await fileToBase64(img);
            if (member && member.use_custom_profile_picture && member.profile_picture) {
                setUndoType('undo_to_previous');
            }
            else {
                setUndoType('undo_to_default');
            }

            updateMember({
                member: {
                    profile_picture: base64.toString(),
                    use_custom_profile_picture: true
                }
            }, { onSuccess: () => {

                setProfilePictureAdded(true);
            } });
        }
    };

    const formatEmail = (inputEmail: string): string => {

        if (inputEmail.length >= 33) {
            return inputEmail.slice(0, 33) + '...';
        }

        return inputEmail;
    };

    return (
        <VStack
            px={'4'}
            w={'100%'}
            alignItems={'center'}
            space={4}
            pb={4}
        >
            <VStack w={'100%'} alignItems='center' space={4}>
                <H1
                    fontWeight={'normal'}
                    text={'profile.greetings'}
                    textValues={[`${member?.first_name || ''} ${member?.last_name || ''}`]}
                    color={text}
                />
                <HStack space={4} w={'100%'} p={2} alignItems={'center'} justifyContent={'space-evenly'} >

                    {<VStack p={3} borderRadius={'md'} w={'48'} h={'48'} alignItems={'center'}>
                        {loadingUpload ? <Loading w={'40'} h={'40'} isLoading={loadingUpload} /> :
                            <Box flex={1}>
                                <Pressable onPress={() => setIsModalOpen(true)}>
                                    {profilePictureUri ? (
                                        <Image
                                            w={'126'}
                                            h={'126'}
                                            borderRadius={126}
                                            uri={profilePictureUri}
                                        />
                                    ) : (
                                        <SpIcon
                                            color={icon}
                                            icon={faUserCircle}
                                            size={126}
                                        />
                                    )}
                                </Pressable>
                                <Pressable onPress={() => setIsModalOpen(true)} py={2} alignItems={'center'}>
                                    <Caption color={text}>
                                        {t('profile.edit')}
                                    </Caption>
                                </Pressable>
                            </Box>
                        }
                    </VStack>}
                    {profile?.profileQRCode && <Divider w={1} h={'120px'} bg={gStyles.button_background}></Divider>}
                    {profile?.profileQRCode && (
                        <Box alignItems={'center'} justifyContent={'center'} borderRadius={'md'} flex={1}>
                            {member && member.phone_number && (
                                <Center>
                                    <QrCode
                                        testID="profile-qrcode"
                                        color={qr_code_color}
                                        bg={qr_code_background}
                                        size={150}
                                        value={(member && member.phone_number) || ''}
                                    />
                                </Center>
                            )}
                        </Box>
                    )}
                </HStack>
                <input style={{ display: 'none' }} type="file" id="myImage" accept="image/*" onChange={onImageChange} />
                <HStack space={4} w={'100%'}>
                    <Button
                        testID='profile-recent-history-button'
                        bg={gStyles.button_background}
                        flex={1}
                        px={4}
                        onPress={() => navigate(`w/${params.wallet_id}/profile/menu/history`)}
                    >
                        <Caption
                            color={gStyles.button_text}
                            text='profileMenu.recent_history'
                        />
                    </Button>
                    <Button
                        testID='profile-my-account-button'
                        bg={gStyles.button_background}
                        flex={1}
                        px={4}
                        onPress={() => navigate(`w/${params.wallet_id}/profile/menu`)}
                    >
                        <Caption
                            color={gStyles.button_text}
                            text='profile.my_account'
                        />
                    </Button>
                </HStack>
            </VStack>


            <VStack alignSelf={'flex-start'} space={3}>
                {member && member.phone_number && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faPhone}
                        />
                        <Caption
                            color={text}
                        ><Caption fontWeight={500}>{t('profile.phone')}</Caption> {member?.phone_number}</Caption>
                    </HStack>
                )}
                {member && member.email && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faEnvelope}
                        />
                        <Caption
                            color={text}
                            overflowX={'hidden'}
                        ><Caption fontWeight={500}>{t('profile.email')}</Caption> {formatEmail(member.email)}</Caption>
                    </HStack>
                )}
                {member && member.birthday && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faBirthdayCake}
                        />
                        <Caption
                            color={text}
                        ><Caption fontWeight={500}>{t('profile.birthday')}</Caption> {dayjs(member.birthday).format('MM/DD/YYYY')}</Caption>
                    </HStack>
                )}
                {member?.address1 && (
                    <HStack alignItems={'center'} space={2}>
                        <SpIcon
                            color={icon}
                            icon={faHome}
                        />
                        <Caption
                            color={text}
                        >{member.address1 || ''} {member.address2 || ''}</Caption>
                    </HStack>
                )}
            </VStack>

            <Sheet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} style={{ width: 'clamp(320px, 100vw, 480px)', height: '400px', marginTop: 'auto', marginRight: 'auto', marginLeft: 'auto' }}>
                <Sheet.Container>
                    <Sheet.Header />
                    <Sheet.Content>
                        <Box w={'100%'}>
                            <Center alignItems='center'>
                                <H4 fontWeight='bold' pb={4}>{t('profile.upload_profile')}</H4>
                                <label htmlFor="myImage" style={{ width: '100%', margin: 'auto' }}>
                                    <Button
                                        testID='import_from_gallery-button'
                                        htmlFor="myImage"
                                        bg={gStyles.button_background}
                                        w={'70%'}
                                        my={2}
                                        mx={'auto'}
                                    >
                                        <Caption
                                            color={gStyles.button_text}
                                            text='profile.choose_from_library'
                                        />
                                    </Button>
                                </label>
                                {profilePictureUri ? <Button
                                    testID='remove-photo-button'
                                    bg={gStyles.button_background}
                                    my={2}
                                    w={'70%'}
                                    onPress={() => {
                                        updateMember({
                                            member: {
                                                use_custom_profile_picture: false
                                            }
                                        }, { onSuccess: () => {
                                            setProfilePictureRemoved(true);
                                        } });
                                    }}
                                >
                                    <Caption color={gStyles.button_text}>
                                        {t('profile.remove_photo')}
                                    </Caption>
                                </Button> : <></>}
                                <Pressable
                                    my={2}
                                    onPress={() => {
                                        setIsModalOpen(false);
                                    }}
                                >
                                    <Caption
                                        color={gStyles.button_background}
                                    >
                                        {t('profile.cancel')}
                                    </Caption>
                                </Pressable>
                                { profilePictureAdded ?
                                    <Box
                                        width={'80%'}
                                        testID='success-toast'
                                        bg="success"
                                        px="6"
                                        py="3"
                                        rounded="sm"
                                        flexDirection='row'
                                        zIndex={999999}
                                        justifyContent={'center'}
                                    >
                                        <Box
                                            flexDirection='row'
                                        >
                                            <Caption color={gStyles.button_text}>
                                                {t('profile.profile_image_added')} <Pressable
                                                    onPress={() => {
                                                        if (undoType) {
                                                            updateMember({
                                                                member: {
                                                                    use_custom_profile_picture: undoType
                                                                }
                                                            });
                                                        }
                                                    }}>
                                                    <Caption color={gStyles.button_text} underline>
                                                        {t('profile.undo')}
                                                    </Caption>
                                                </Pressable>
                                            </Caption>
                                        </Box>
                                    </Box>
                                    : <></> }
                                { profilePictureRemoved ?
                                    <Box
                                        width={'80%'}
                                        testID='success-toast'
                                        bg="success"
                                        px="6"
                                        py="3"
                                        rounded="sm"
                                        flexDirection='row'
                                        zIndex={999999}
                                        justifyContent={'center'}
                                    >
                                        <Box
                                            flexDirection='row'
                                        >
                                            <Caption color={gStyles.button_text} textAlign={'center'}>
                                                {t('profile.profile_image_removed')} <Pressable
                                                    onPress={() => {
                                                        updateMember({
                                                            member: {
                                                                use_custom_profile_picture: true
                                                            }
                                                        });
                                                    }}>
                                                    <Caption color={gStyles.button_text} underline>
                                                        {t('profile.undo')}
                                                    </Caption>
                                                </Pressable>
                                            </Caption>
                                        </Box>
                                    </Box>
                                    : <></> }
                            </Center>
                        </Box>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop />
            </Sheet>
        </VStack>
    );
};


export const ProfileHeaderSkeleton = () => {
    return (
        <VStack space={6} alignItems="center" mt={8} w='100%'>
            <Skeleton
                w={'175px'}
                h={5}
                rounded={'md'}
            />
            <HStack space={4}>
                <Skeleton
                    h={10}
                    w={32}
                    rounded="md"
                />
                <Skeleton
                    h={10}
                    w={32}
                    rounded="md"
                />
            </HStack>
            <VStack space={2} alignItems="flex-start" w='100%'>
                <HStack space={2} alignItems="center">
                    <Center h={6} w={6}>
                        <Skeleton
                            h={4}
                            w={4}
                            rounded="full"
                        />
                    </Center>
                    <Skeleton.Text
                        lines={1}
                        width={80}
                    />
                </HStack>
                <HStack space={2} alignItems="center">
                    <Center h={6} w={6}>
                        <Skeleton
                            h={4}
                            w={4}
                            rounded="full"
                        />
                    </Center>
                    <Skeleton.Text
                        lines={1}
                        width={80}
                    />
                </HStack>
                <HStack space={2} alignItems="center">
                    <Center h={6} w={6}>
                        <Skeleton
                            h={4}
                            w={4}
                            rounded="full"
                        />
                    </Center>
                    <Skeleton.Text
                        lines={1}
                        width={40}
                    />
                </HStack>
            </VStack>
        </VStack>
    );
};

export default ProfileHeader;
